import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { ROUTES } from '../constants/routes';
import { emailRegex } from '../regex';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';

import { FormServerError } from '../types';

import { UserEmailData } from '../api/userSettings';

import {
  ButtonMain,
  Paper,
  UnderlinedLink,
} from '../atoms';

import {
  InputTextWithLabel,
  InputBaseContainer,
} from '../molecules';

const schema = yup.object().shape({
  email: yup
    .string()
    .matches(emailRegex, 'Enter a valid email address')
    .required()
    .label('Email'),
});

const ChangeEmailForm = ({
  currentEmail,
  onSubmit,
  initialErrors,
}: ChangeEmailFormTypes) => {
  const history = useHistory();

  const {
    register,
    handleSubmit,
    errors,
    setError,
  } = useForm<UserEmailData>({
    resolver: yupResolver(schema),
  });

  const [emailBlurError, setEmailBlurError] = React.useState('');

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<UserEmailData>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  return (
    <PaperBox>
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(onSubmit)}
      >
        <InputBaseContainer>
          <InputTextWithLabel
            label="Your current Email"
            size="small"
            value={currentEmail}
            disabled
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="email"
            type="email"
            error={errors.email || (emailBlurError ? { message: emailBlurError } : undefined)}
            inputRef={register}
            label="Enter new Email"
            placeholder="user@example.com"
            isRequired={isYupFieldRequired('email', schema)}
            onBlur={e => {
              const val = e.target.value;
              if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setEmailBlurError('Please enter a valid email address');
              } else {
                setEmailBlurError('');
              }
            }}
            onChange={() => { if (emailBlurError) setEmailBlurError(''); }}
          />
        </InputBaseContainer>
        <ButtonContainer>
          <ButtonMain type="submit">
            Update information
          </ButtonMain>

          <CancelAction
            onClick={() => history.push(ROUTES.ACCOUNT)}
          >
            Cancel
          </CancelAction>
        </ButtonContainer>
      </FormContainer>
    </PaperBox>
  );
};

const PaperBox = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 480px;
  background: var(--secondary);
  padding: 24px 24px 28px;
  margin-top: 7px;

  .MuiFormControl-root {
    width: 100%;
    margin-bottom: 24px;
  }

  .MuiButton-root {
    width: 250px;

    &.MuiButton-contained {
      box-shadow: none;
    }
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  text-align: center;
`;

const CancelAction = styled(UnderlinedLink).attrs({
  as: 'span',
})`
  display: block;
  margin-top: 24px;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

interface ChangeEmailFormTypes {
  currentEmail: string;
  onSubmit: (data: UserEmailData) => void;
  initialErrors?: FormServerError<UserEmailData>;
}

export default ChangeEmailForm;
